import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight } from "lucide-react";

const BRAND = {
  burgundy: "#800020",
  gold: "#b08d57",
  deepGreen: "#006400",
  charcoal: "#0b0b0f",
};

function Rule({ className = "" }) {
  return <div className={"h-px w-full bg-black/10 " + className} />;
}

function Box({ children, className = "" }) {
  return (
    <div className={"border border-black/15 bg-white " + className}>
      {children}
    </div>
  );
}

function DarkBox({ children, className = "" }) {
  return (
    <div
      className={"border border-neutral-950 text-white " + className}
      style={{ backgroundColor: BRAND.charcoal }}
    >
      {children}
    </div>
  );
}

function TagPill({ children, tone = "gold" }) {
  const colour =
    tone === "burgundy"
      ? BRAND.burgundy
      : tone === "deepGreen"
      ? BRAND.deepGreen
      : BRAND.gold;

  return (
    <span
      className="inline-flex items-center border px-2 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold"
      style={{
        borderColor: colour + "66",
        backgroundColor: colour + "12",
      }}
    >
      {children}
    </span>
  );
}

function AboutHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <Link
                to="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Africa Loud Editorial
              </Link>

              <Link
                to="/echoes"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Echoes
              </Link>

              <Link
                to="/film"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                African Film
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <Link
                to="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </Link>

              <Link
                to="/collab"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Collab
              </Link>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-center gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">
                Culture intelligence creative execution
              </div>
            </div>

            <Link to="/" className="mx-auto flex items-center gap-4 text-center">
              <div className="text-left">
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl leading-none">
                  Africa Loud
                </div>
                <div className="mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </Link>

            <div className="hidden justify-self-end sm:block text-right">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-400">
                JHB / LAG / KGL
              </div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </Link>

              <Link
                to="/film"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                African Film
              </Link>

              <Link
                to="/echoes"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Echoes
              </Link>

              

              <Link
                to="/collab"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Collab
              </Link>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Subscribe
              </div>
              <div className="h-3 w-px bg-black/15" />
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function HeroSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6">
      <div className="grid gap-6 pt-8 lg:grid-cols-[1.15fr_0.85fr]">
        <Box className="p-7 sm:p-12 bg-[#fafafa]">
          <div className="flex items-center gap-3">
            <div
              className="h-2 w-2"
              style={{ backgroundColor: BRAND.burgundy }}
            />
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-600">
              About Africa Loud
            </div>
          </div>

          <h1 className="mt-8 font-serif text-5xl leading-[1.02] tracking-tight text-neutral-950 sm:text-7xl">
            The cultural intelligence platform for Africa's creative future.
          </h1>

          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-neutral-600">
            Africa Loud is an editorial and cultural intelligence platform
            tracking how African art, music, fashion, film, youth behaviour,
            identity, and digital participation shape the global story.
          </p>

          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-600">
            We exist to document the movements, decode the signals, and build
            the strategic language around African creativity before the world
            catches up.
          </p>
        </Box>

        <Box className="p-7 sm:p-9 flex flex-col justify-between min-h-[420px]">
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-neutral-500">
              Our Position
            </div>

            <h2 className="mt-5 font-serif text-3xl leading-tight text-neutral-950">
              Africa is already producing the future.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-neutral-600">
              Africa Loud gives that future language, structure, visibility,
              and commercial imagination. We are interested in culture as
              memory, movement, market, and meaning.
            </p>
          </div>

          <div className="mt-10 border-t border-black/10 pt-6">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-400">
              Core Belief
            </div>
            <div className="mt-2 font-serif text-xl text-neutral-950">
              Culture is infrastructure.
            </div>
          </div>
        </Box>
      </div>
    </section>
  );
}

function MissionSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8">
      <div className="grid gap-6 lg:grid-cols-3">
        <Box className="p-7">
          <TagPill tone="burgundy">Mission</TagPill>
          <h2 className="mt-5 font-serif text-3xl leading-tight">
            To redefine Africa's global narrative.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600">
            We curate, analyse, and amplify authentic African cultural
            expression across music, fashion, art, film, language, digital
            behaviour, and creative enterprise.
          </p>
        </Box>

        <Box className="p-7">
          <TagPill>Vision</TagPill>
          <h2 className="mt-5 font-serif text-3xl leading-tight">
            A billion-rand cultural enterprise.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600">
            Africa Loud is building towards a future where African creativity is
            treated as a central force in global media, brand strategy, and
            cultural commerce.
          </p>
        </Box>

        <Box className="p-7">
          <TagPill tone="deepGreen">Purpose</TagPill>
          <h2 className="mt-5 font-serif text-3xl leading-tight">
            To connect Africa to the world with clarity.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600">
            We create the editorial, strategic, and cultural systems that help
            audiences, brands, and creatives understand what is moving and why
            it matters.
          </p>
        </Box>
      </div>
    </section>
  );
}

function WhatWeDoSection() {
  const items = [
    {
      title: "Editorial Culture",
      text:
        "Essays, features, profiles, opinion pieces, and cultural reporting across African music, fashion, art, film, youth identity, and digital life.",
    },
    {
      title: "Cultural Intelligence",
      text:
        "Signal mapping, subculture analysis, trend interpretation, adoption patterns, and insight frameworks for African creative markets.",
    },
  
     ];

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
      <div className="mb-6">
        <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-500">
          What We Do
        </div>
        <h2 className="mt-3 font-serif text-4xl tracking-tight text-neutral-950">
          We turn cultural movement into meaning.
        </h2>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {items.map((item) => (
          <Box key={item.title} className="p-7">
            <h3 className="font-serif text-2xl tracking-tight text-neutral-950">
              {item.title}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              {item.text}
            </p>
          </Box>
        ))}
      </div>
    </section>
  );
}

function MeetTheTeamSection() {
  const team = [
    {
      name: "Nolwazi M.",
      role: "Founder and Creative Strategist",
      text:
        "Leads Africa Loud's cultural intelligence, editorial direction, creative strategy, and long-term vision for African youth culture, media, and creative commerce.",
    },
    {
      name: "Editorial Contributors",
      role: "Writers, thinkers, and cultural observers",
      text:
        "A growing network of voices contributing essays, features, reflections, interviews, and cultural analysis across the continent and diaspora.",
    },
    {
      name: "Creative Collaborators",
      role: "Designers, filmmakers, artists, and strategists",
      text:
        "Project-based collaborators who help shape Africa Loud's visual language, campaigns, cultural concepts, and future-facing editorial systems.",
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
      <div className="mb-6">
        <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-500">
          Meet The Team
        </div>
        <h2 className="mt-3 font-serif text-4xl tracking-tight text-neutral-950">
          The people building the Africa Loud world.
        </h2>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {team.map((member) => (
          <Box key={member.name} className="p-7">
            <div className="h-24 w-24 rounded-full border border-black/10 bg-[#fafafa] flex items-center justify-center">
              <span className="font-serif text-3xl text-neutral-950">
                {member.name.charAt(0)}
              </span>
            </div>

            <h3 className="mt-6 font-serif text-2xl tracking-tight text-neutral-950">
              {member.name}
            </h3>

            <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">
              {member.role}
            </div>

            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              {member.text}
            </p>
          </Box>
        ))}
      </div>
    </section>
  );
}

function EditorialPillarsSection() {
  const pillars = [
    "Music X Culture",
    "Art X Fashion",
    "African Film",
    "Echoes",
    "AMS",
    "Cultural Intelligence",
  ];

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
      <Box className="p-7 sm:p-10 bg-[#fafafa]">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <div>
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-500">
              Our Editorial World
            </div>
            <h2 className="mt-3 font-serif text-4xl leading-tight tracking-tight text-neutral-950">
              A platform built around African cultural systems.
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            {pillars.map((pillar) => (
              <span
                key={pillar}
                className="border border-black/15 bg-white px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700"
              >
                {pillar}
              </span>
            ))}
          </div>
        </div>
      </Box>
    </section>
  );
}

function ClosingSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
      <DarkBox className="p-7 sm:p-10">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-white/50">
              Work With Us
            </div>
            <h2 className="mt-3 font-serif text-3xl leading-tight text-white">
              Building for African culture, creativity, and influence?
            </h2>
          </div>

          <div>
            <p className="text-sm leading-relaxed text-white/75">
              Africa Loud works across editorial, cultural strategy, creative
              concepts, partnership development, campaign thinking, and African
              youth culture intelligence.
            </p>

            <Link
              to="/collab"
              className="mt-6 inline-flex items-center justify-center border border-white/30 px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-white hover:bg-white hover:text-black transition"
            >
              Start a collaboration
              <ArrowRight className="ml-2 h-3 w-3" />
            </Link>
          </div>
        </div>
      </DarkBox>
    </section>
  );
}

function AboutFooter() {
  return (
    <footer className="mt-12 bg-neutral-900 px-5 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-lg font-bold text-white font-serif">
            Africa Loud
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link
              to="/"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              About
            </Link>
            <Link
              to="/echoes"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              Echoes
            </Link>
            <Link
              to="/film"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              African Film
            </Link>
            <Link
              to="/ams"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              AMS
            </Link>
            <Link
              to="/collab"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              Collab
            </Link>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 text-[10px] tracking-widest uppercase text-white/40">
          &copy; 2026 Africa Loud. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <Helmet>
        <title>About Us | Africa Loud</title>
        <meta
          name="description"
          content="Africa Loud is an editorial and cultural intelligence platform documenting African creativity, youth culture, music, fashion, film, art, and digital movements."
        />
      </Helmet>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;700;800&family=Outfit:wght@300;400;600;700&display=swap');

          body, div, p, span, button, a {
            font-family: 'Outfit', sans-serif;
          }

          .font-serif, h1, h2, h3, h4 {
            font-family: 'Fraunces', serif !important;
            font-weight: 700;
          }
        `}
      </style>

      <AboutHeader />
      <HeroSection />
      <MissionSection />
      <WhatWeDoSection />
      <MeetTheTeamSection />
      <EditorialPillarsSection />
      <ClosingSection />
      <AboutFooter />
    </div>
  );
}